import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import api from "@/lib/api";
import { toast } from "sonner";

export default function UploadReading() {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Choose a file first.");
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: doc } = await api.post("/documents/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const { data: sess } = await api.post("/sessions/start", {
        source: "upload",
        source_id: doc.doc_id,
        title: doc.title,
        content: doc.content,
      });
      navigate(`/reading/${sess.session_id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-forge-900 text-forge-100">
      <Header />
      <main className="max-w-3xl mx-auto px-6 lg:px-10 py-16 sm:py-24">
        <div className="text-[10px] tracking-[0.4em] uppercase text-ember-500 mb-4">Upload</div>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mb-3" data-testid="upload-title">
          Bring your own reading.
        </h1>
        <p className="text-sm text-forge-100/60 mb-10">
          Upload a PDF, TXT, or DOCX file. We will extract the text, identify challenging vocabulary, and prepare questions for reflection.
        </p>

        <form onSubmit={handleUpload} className="space-y-6">
          <label
            htmlFor="file-input"
            className="block border border-dashed border-white/20 hover:border-ember-500 rounded-sm p-12 text-center cursor-pointer transition-colors"
          >
            <div className="text-[10px] uppercase tracking-[0.3em] text-ember-500 mb-3">PDF · TXT · DOCX</div>
            <div className="font-serif text-2xl text-forge-100 mb-2">
              {file ? file.name : "Choose a file"}
            </div>
            <div className="text-xs text-forge-100/40">{file ? `${(file.size / 1024).toFixed(1)} KB` : "Up to 10 MB"}</div>
            <input
              id="file-input"
              type="file"
              accept=".pdf,.txt,.docx"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="hidden"
              data-testid="upload-file-input"
            />
          </label>
          <button
            type="submit"
            disabled={loading || !file}
            className="w-full px-5 py-3 bg-ember-500 hover:bg-ember-400 disabled:opacity-60 text-forge-900 font-semibold tracking-wide rounded-sm transition-all"
            data-testid="upload-submit"
          >
            {loading ? "Forging your reading…" : "Begin Reading"}
          </button>
        </form>
      </main>
    </div>
  );
}
